// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  Column,
  H1,
  InputCheckbox,
  LgPadding,
  LgSpacing,
  MdPadding,
  Row,
  Toast,
  ToastContext,
} from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { updateUser } from '~shared/actions/users';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import getUserPhiAuth from '~shared/features/users/selectors/getUserPhiAuth';
import { type UserPhiAuthValues } from '~shared/helpers/communicationPreferences';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { PhiAuthCommsPreferencesText, PhiAuthCommsPreferencesTextWithoutPush } from '~web/constants';

export const PhiAuthActionSheet = () => {
  'use no memo';
  const dispatch = useDispatchShared();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);
  const { trackEvent } = useAnalytics();
  const userPhiAuth = useSelectorShared(getUserPhiAuth);
  const { isFeatureDisabled: shouldDisablePushNotification, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('push_notification');

  useEffect(() => {
    trackEvent({
      event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_VIEWED,
    });
  }, [trackEvent]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      sms_authorized: userPhiAuth?.sms_authorized || false,
      push_authorized: userPhiAuth?.push_authorized || false,
      email_authorized: userPhiAuth?.email_authorized || false,
    },
  });

  const smsAuthController = useController({
    control,
    name: 'sms_authorized',
    rules: { required: false },
  });
  const emailAuthController = useController({
    control,
    name: 'email_authorized',
    rules: { required: false },
  });
  const pushAuthController = useController({
    control,
    name: 'push_authorized',
    rules: { required: false },
  });

  const onSubmit = async ({ sms_authorized, email_authorized, push_authorized }: UserPhiAuthValues) => {
    const phi_auth_data = {
      sms_authorized,
      email_authorized,
      push_authorized: shouldDisablePushNotification ? false : push_authorized,
    };
    const { success } = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateUser({
        phi_auth_data: JSON.stringify(phi_auth_data),
      }),
    );

    if (success) {
      addToast(<Toast>Preferences saved! To make changes, visit Account.</Toast>);
      trackEvent({ event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_SUBMITTED });
    } else {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
    closeActionSheet();
  };

  const handleClose = async () => {
    const phi_auth_data = {
      sms_authorized: null,
      email_authorized: null,
      push_authorized: null,
    };
    const { success } = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateUser({
        phi_auth_data: JSON.stringify(phi_auth_data),
      }),
    );

    if (success) {
      addToast(<Toast>Preferences saved! To make changes, visit Account.</Toast>);
      trackEvent({ event: EVENTS.PHI_PREFERENCES_INTERSTITIAL_DISMISSED });
    } else {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
    closeActionSheet();
  };

  return (
    <ActionSheetV2
      analyticsName="phi_auth"
      handleClose={handleClose}
    >
      <H1 center>Health information preferences</H1>
      <Column>
        <LgPadding bottomPadding={SPACING.STATIC.NONE}>
          <Body>
            {shouldDisablePushNotification || isFeatureDisablingLoading
              ? PhiAuthCommsPreferencesTextWithoutPush
              : PhiAuthCommsPreferencesText}
          </Body>
        </LgPadding>
      </Column>
      <LgSpacing />
      <Column gap="XS">
        {shouldDisablePushNotification || isFeatureDisablingLoading ? null : (
          <InputCheckbox
            name="push_authorized"
            label="Push notifications"
            onPress={() => {
              pushAuthController.field.onChange(!pushAuthController.field.value);
            }}
            value={pushAuthController.field.value}
          />
        )}
        <InputCheckbox
          name="sms_authorized"
          label="Text messages/SMS"
          onPress={() => {
            smsAuthController.field.onChange(!smsAuthController.field.value);
          }}
          value={smsAuthController.field.value}
        />
        <InputCheckbox
          name="email_authorized"
          label="Emails"
          onPress={() => {
            emailAuthController.field.onChange(!emailAuthController.field.value);
          }}
          value={emailAuthController.field.value}
        />
        <Row center>
          <MdPadding>
            <Button
              label="Submit"
              accessibilityLabel="Press to submit contact preferences"
              onPress={handleSubmit(onSubmit)}
              loading={isSubmitting}
              loadingLabel="Submitting"
            />
          </MdPadding>
        </Row>
      </Column>
    </ActionSheetV2>
  );
};
